import useSearchFiles from "../../hooks/use-search-files";
import Card from "../UI/Card";
import { Selectable } from "../UI/Selectable";
import useTenants from "../../hooks/use-tenants";
import { useState } from "react";
import TextInputStandalone from "../UI/TextInputStandalone";
import moment from "moment/moment";
import Button from "../UI/Button";
import { DocType } from "../../utils/constants/documents";
import useLoader from "../../hooks/use-loader";
import DocumentDownloader from "../UI/DocumentDownloader";
import useProperties from "../../hooks/use-properties";
import DocumentDownloadLink from "../UI/DocumentDownloadLink";
import styles from "./ProfileDocuments.module.css";
import { House, Oeil } from "../UI/Icons";
import useMediaQuery from "../../hooks/use-media-query";
import { useModal } from "../../hooks/use-modal.jsx";
import GenerateAllQuittances from "./GenerateAllQuittances";
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import useProfileFilter from "../../hooks/use-profile-filter";
import { ReactSelectStandalone } from "../UI/SelectStandalone";

export default function ProfileDocuments() {
  const initialFrom = moment().startOf("year").format("YYYY-MM-DD");
  const initialTo = moment().format("YYYY-MM-DD");
  const { tenantsById, generateAllQuittances } = useTenants({ withById: true });
  const { propertiesById } = useProperties({ withById: true });
  const { profileBailleurs } = useProfileBailleurs();
  const [
    GenererQuittanceModal,
    closeGenererQuittanceModal,
    openGenererQuittanceModal,
  ] = useModal();

  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);
  const [profileBailleurFilter, setProfileBailleurFilter] = useState([]);
  const { selectedProfileIds } = useProfileFilter();
  const effectiveProfileBailleurFilter =
    profileBailleurFilter.length > 0
      ? profileBailleurFilter
      : selectedProfileIds;
  const { files, isLoading, generateZip } = useSearchFiles({
    date_from: dateFrom,
    date_to: dateTo,
    type: DocType.QUITTANCE,
    profile_bailleur_ids: effectiveProfileBailleurFilter,
  });
  const [isZipping, zip] = useLoader(generateZip);
  const isSmallScreen = useMediaQuery("(max-width: 600px)");

  async function generateSignedUrl(file_ids, setSignedUrl) {
    try {
      const response = await zip(file_ids);
      setSignedUrl(response.data);
    } catch (e) {
      console.error(e);
    }
  }

  async function generateAllQuittancesHandler() {
    await generateAllQuittances();
    closeGenererQuittanceModal();
  }

  return (
    <>
      <GenererQuittanceModal>
        <GenerateAllQuittances onSubmit={generateAllQuittancesHandler} />
      </GenererQuittanceModal>
      <Card>
        <p className={"text-center marginB10 primaryText"}>
          Seules les quittances déjà générées apparaîtront dans cette liste.{" "}
          <Button variant="tertiary" onClick={openGenererQuittanceModal}>
            Cliquez ici
          </Button>{" "}
          pour générer toutes les quittances
        </p>
        <Selectable items={files}>
          <div className={"flex wrap gap10 space-between marginB20"}>
            <div className={"flex wrap gap10 align-items-center"}>
              <label>
                <Selectable.Checkbox selectAll /> Tout sélectionner
              </label>
              <TextInputStandalone
                onChange={(e) => setDateFrom(e.target.value)}
                label={"Depuis"}
                type={"date"}
                value={dateFrom}
              />
              <TextInputStandalone
                onChange={(e) => setDateTo(e.target.value)}
                label={"Jusqu'à"}
                type={"date"}
                value={dateTo}
              />
              <ReactSelectStandalone
                name="profile_bailleur"
                label={"Profil bailleur"}
                isMulti
                options={profileBailleurs.map((p) => ({
                  value: p.id,
                  label: p.nom_profile,
                }))}
                value={profileBailleurFilter
                  .map((id) => {
                    const profile = profileBailleurs.find((p) => p.id === id);
                    return profile
                      ? { value: profile.id, label: profile.nom_profile }
                      : null;
                  })
                  .filter(Boolean)}
                onChange={(options) =>
                  setProfileBailleurFilter(options.map((o) => o.value))
                }
              />
            </div>
            <div>
              <Button
                className={"marginB5"}
                onClick={() => {
                  setDateFrom(initialFrom);
                  setDateTo(initialTo);
                  setProfileBailleurFilter([]);
                }}
              >
                Reset
              </Button>
              <DocumentDownloader>
                {(setSignedUrl) => (
                  <Selectable.ActionButton
                    disabled={isLoading}
                    isLoading={isZipping}
                    resetOnClick
                    onClick={(fileIds) => {
                      if (fileIds.length > 0)
                        return generateSignedUrl(fileIds, setSignedUrl);
                    }}
                  >
                    Télécharger
                  </Selectable.ActionButton>
                )}
              </DocumentDownloader>
            </div>
          </div>
          {files.map((file, i) => (
            <Card key={file.id} padding={"md"} className={"marginB5"}>
              <label className={styles.grid}>
                <div>
                  <Selectable.Checkbox value={file.id} index={i} />
                  <span className={"primaryText marginL5"}>
                    {tenantsById[file.id_tenant]?.nom || "Inconnu"}{" "}
                  </span>
                </div>
                {!isSmallScreen && (
                  <div className={"flex gap10 align-items-center"}>
                    <House />
                    {propertiesById[tenantsById[file.id_tenant]?.id_bien]
                      ?.nom || "Inconnu"}{" "}
                  </div>
                )}
                <div>
                  {moment(file.meta["date_quittance"]).format("MMMM YYYY")}
                </div>
                <div>
                  <DocumentDownloadLink documentId={file.id} file={file}>
                    <Oeil stroke={"#0C69F5"} />
                  </DocumentDownloadLink>
                </div>
              </label>
            </Card>
          ))}
        </Selectable>
      </Card>
    </>
  );
}
